import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { AiOutlineLoading3Quarters, AiOutlineArrowLeft } from 'react-icons/ai';
import apiService from '../services/apiService';

const PageContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px; /* Space for header */
  padding-bottom: 50px;
  background-color: #f8f9fa;
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: #2E7D32;
  font-weight: 600;
  margin-bottom: 20px;
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover {
    color: #1B5E20;
    text-decoration: underline;
  }
`;

const NewsDate = styled.p`
  font-size: 1rem;
  color: #777;
  margin-bottom: 20px;
`;

const NewsTitle = styled.h1`
  color: #2E7D32;
  font-size: 2.5rem;
  margin-bottom: 20px;
  font-weight: 700;
`;

const NewsText = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  margin-bottom: 40px;
  white-space: pre-wrap;
`;

const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin: 40px 0;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 250px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const NewsImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const LoadingSpinner = styled(AiOutlineLoading3Quarters)`
  animation: spin 1s linear infinite;
  font-size: 3rem;
  color: #2E7D32;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const NotFoundMessage = styled.div`
  text-align: center;
  padding: 50px 0;
  
  h2 {
    color: #2E7D32;
    margin-bottom: 20px;
  }
  
  p {
    font-size: 1.1rem;
    color: #555;
    margin-bottom: 30px;
  }
`;

const SingleNewsPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        // Fetch the post data
        const posts = await apiService.getNewsPosts();
        const currentPost = posts.find(p => p._id === id);
        
        if (!currentPost) {
          setError('News post not found');
          setLoading(false);
          return;
        }
        
        // Fetch the images for this post
        const images = await apiService.getNewsPostImages(id);
        setPost({ ...currentPost, images });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching news post:', error);
        setError('Failed to load news post');
        setLoading(false);
      }
    };

    if (id) {
      fetchPost();
    }
  }, [id]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <PageContainer>
        <ContentContainer>
          <BackButton to="/news">
            <AiOutlineArrowLeft style={{ marginRight: '8px' }} /> Back to News
          </BackButton>
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        </ContentContainer>
      </PageContainer>
    );
  }

  if (error || !post) {
    return (
      <PageContainer>
        <ContentContainer>
          <BackButton to="/news">
            <AiOutlineArrowLeft style={{ marginRight: '8px' }} /> Back to News
          </BackButton>
          <NotFoundMessage>
            <h2>News Post Not Found</h2>
            <p>The news post you're looking for could not be found.</p>
          </NotFoundMessage>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        <BackButton to="/news">
          <AiOutlineArrowLeft style={{ marginRight: '8px' }} /> Back to News
        </BackButton>
        
        <NewsDate>{formatDate(post.postDate || post.createdAt)}</NewsDate>
        <NewsTitle>Cocoa-Pro News</NewsTitle>
        
        <NewsText>{post.text}</NewsText>
        
        {post.images && post.images.length > 0 && (
          <ImageGallery>
            {post.images.map((image, index) => (
              <ImageContainer key={index}>
                <NewsImage src={image} alt={`News image ${index + 1}`} />
              </ImageContainer>
            ))}
          </ImageGallery>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default SingleNewsPost;
